import React, {useState} from 'react'
import _ from 'lodash/fp'
import {Button, ContextMenu, Dialog, Icon, Li, Loading, WarningDialog} from '@startlibs/components'
import {BrandFooter, Card, CardHeader, SignInLayout} from '../components/SigninLayout'
import {usePopupToggle, useToggle} from '@startlibs/core'
import {SimpleCheckbox} from '@startlibs/form'
import styled, {css} from 'styled-components'
import {formatDate, getColor, media} from '@startlibs/utils'
import {getFetcherWithBearer, postFetcherWithBearer, shareJwtGetFetcher} from '../utils/authFetch'
import {checkForDownload, downloadFile} from '../utils/downloadFile'
import {dicomDateToDate} from '../utils'
import {lighten} from 'polished'
import {DownloadErrorDialog} from './DownloadErrorDialog'
import {HelpButton} from "../components/HelpButton";
import {SPA_ORIGIN} from './ShareDialog'
import {DATE_FORMAT} from '../utils/DateFormat'

const SharedRecordCard = styled(Card) `
  ${media.max(410)`
    padding: 2rem 1.5rem;
  `}
  min-width: 546px;
`

const StyledRow = styled.div`
  &:nth-child(odd) {
    background-color: ${getColor('rowHighlight')};
  }
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  // justify-content: flex-start;
  align-items: center;
  border-bottom: 1px solid ${getColor('gray210')};
  padding: 1rem;
  &:last-child {
    border-bottom: 0px;
  }
  .rowDetails {
    margin-top: 2px;
    font-size: 12px;
  }
  label {
    margin-bottom: 0;
  }
  ${props => props.selectable && css`
    cursor: pointer;
    &:hover {
      background: rgba(0,139,210,0.15);
    }
  `}
  ${props => props.selected && css`
    &:nth-child(odd), &:nth-child(even) {
      background-color: ${getColor('main')};
      color: white;
      &:hover {
        background: ${getColor('main')};
      }
    }
  `}
  ${props => props.unavailable && css`
    &:nth-child(odd), &:nth-child(even) {
      background-color: ${getColor('gray240')};
    }
    color: ${getColor('gray150')};
    b, div {
      color: ${getColor('gray150')};
    }
    cursor: default;
  `}
`

const ListArea = styled.div`
  border: 1px solid ${getColor('gray210')};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  margin-bottom: 1rem;
`

const PatientContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`

const ActionLink = styled.div`
  color: ${getColor('actionLink')};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  margin-left: 0.5rem;
  :hover {
    color: ${getColor('actionLinkHover')};
  }
  ${Icon} {
    margin-left: 8px;
    margin-right: 6px;
    vertical-align: -6px;
    font-size: 24px;
  }
`

const ActionDropdown = styled.div`
  position: relative;
  background: ${getColor('actionDropdownBg')};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  flex-direction: row;
  align-content: center;
  justify-content: center;
  align-items: center;
  margin-left: 12px;
  color: ${getColor('actionLink')};
  cursor: pointer;
  :hover {
    background: ${getColor('actionDropdownHoverBg')};
    color: ${getColor('actionLinkHover')};
  }
`
const DownloadStarted = styled(Icon)`
  position: relative;
  color: white;
  background: ${getColor('success')};
  width: 24px;
  height: 24px;
  line-height: 25px;
  text-align: center;
  font-size: 17px;
  margin-left: 1rem;
  border-radius: 50%;
  font-weight: bold;
`

const PatientName = styled.div `
  font-weight: 600;
  font-size: 14px;
  text-transform: capitalize;
`
const PatientDob = styled.div`
  font-size: 12px;
  color: #787878;
  margin-top: 0.25rem;
`
const DownloadButton = styled(Button) `
  ${Loading} {
    display: inline-block;
    margin: -5px 6px -5px 0;
  }
  ${props => props.preparingDownload && css`
    background-color: ${props => lighten(0.525, props.theme.colors.main)};
    color: ${getColor('main')};
    cursor: default;
    pointer-events: none;
    box-shadow: none;
    :after {
      content: none !important;
    }
  `}
  ${props => props.downloadStarted && css`
    background-color: ${props => lighten(0.46, props.theme.colors.success)};
    color: ${getColor('success')};
    cursor: default;
    pointer-events: none;
    box-shadow: none;
    :after {
      content: none !important;
    }
  `}
  ${Icon} {
    margin-left: 4px;
    margin-right: -0.5rem;
    vertical-align: -1px;
    font-size: 11px;
  }
`
const CardActionsContainer = styled.div `
  margin: 1.5rem auto 0;
  display: flex;
  justify-content: center;
  ${Button} {
    flex-shrink: 1;
    flex-basis: 50%;
  }
  ${media.max(390)`
    display: block;
    ${Button} {
      width: 100%;
      & ~ ${Button} {
        margin-left: 0;
        margin-top: 0.75rem;
      }
    }
  `}
`

const formatAvailableSignatures = _.update('success.availableSignatures',_.flow(_.map(v => [v.signatureId,v]),_.fromPairs))


function shouldShowActionDropdown(shareStudyReport, item, allowImageDownload) {
  // Checks if 'Download report' option should be visible
  const isDownloadReportVisible = shareStudyReport && item && item.report;
  // Checks if any 'Download images' options should be visible
  const isDownloadImagesVisible = allowImageDownload;

  return isDownloadReportVisible || isDownloadImagesVisible;
}

export const SharedStudy = ({providerInfo,navigate,token,viewReport,isCustodian=false,shareInfo,shareSession}) => {
  if (!token) {
    navigate('/')
    return null
  }

  const needHelp = providerInfo.needHelp

  if (!shareSession) {
    navigate('/share/'+token)
    return null
  }

  const {jwt, data: shareData} = shareSession

  const {patientName,patientDOB,reportJWT,sharingOptions,allowImageDownload,shareStudyReport,additionalStudies, studyDescription, studyDate, modality, referringPhysician, institution, images, studyUID, studyAvailability, ...rest} = shareData

  // TODO - Get info from main study
  // const isOldSingleShare = additionalStudies.length === 0 && !studyDescription 
  const isOldSingleShare = Array.isArray(additionalStudies) && additionalStudies.length === 0 && !studyDescription;

  
  // const studyDescription = 'MAIN STUDY DESC'
  // const studyDate = '2023-10-20'
  // const modality = 'CR'
  // const images = 10
  // const studyUID = ''
  // const referringPhysician = ''
  // const institution = ''
  
  const listOfStudies = additionalStudies ? [...additionalStudies] : []
 
  // Add the original shared study first in the list of studies.
  listOfStudies.unshift({
    studyUID: studyUID || '',
    referringPhysician: referringPhysician || '',
    institution: institution || '',
    studyDescription: studyDescription || '',
    modalities: modality || '',
    studyDate: studyDate || '',
    images: images || '',
    report: reportJWT,
    jwt: jwt
  })

  // Map studyAvailability onto each study
  if (Array.isArray(studyAvailability)) {
    const availabilityMap = new Map(studyAvailability.map(sa => [sa.studyUID, sa.available]))
    listOfStudies.forEach(study => {
      if (availabilityMap.has(study.studyUID)) {
        study.unavailable = !availabilityMap.get(study.studyUID)
      }
    })
  }

  if(listOfStudies.length > 0){
    // Assign a sort index to each study
    listOfStudies.forEach((study, index) => {
      study.sortIndex = index;
    });

    listOfStudies.sort((a, b) => {
      if (a.unavailable !== b.unavailable) {
        return a.unavailable ? 1 : -1;
      }
      const dateA = a.studyDate ? new Date(a.studyDate) : new Date(0); // Handle missing date
      const dateB = b.studyDate ? new Date(b.studyDate) : new Date(0); // Handle missing date

      if (dateA.getTime() === dateB.getTime()) {
          return a.sortIndex - b.sortIndex; // Preserve original order if dates are equal
      }
      return dateB - dateA; // Sort by date otherwise
    });
  }
  

  const started = useToggle()

  const downloadLoading = useToggle()
  const downloadLoadingIndicator = useToggle()
  const viewLoading = useToggle()
  const viewImagesDialog = useToggle()
  const [selectedStudies,setSelectedStudies] = useState([])

  const reportLoading = useToggle()
  const reportViewer = useToggle()
  const [report,setReport] = useState()
  const [isLoadingViewAll,setIsLoadingViewAll] = useState(false)

  const downloadMenu = usePopupToggle()
  const downloadError = useToggle()
  const tokenExpiredDialog = useToggle()

  const validateToken = (token) =>
    fetch(SPA_ORIGIN() + '/api/share/tokenValidate', {
      method: 'HEAD',
      headers: {'Authorization': `Bearer ${token}`}
    }).then(res => {
      if (!res.ok) throw new Error()
    })

  const openTokenExpiredDialog = () => {
    tokenExpiredDialog.open()
  }

  const openReportRow = (jwtReport) => {
    validateToken(jwtReport)
      .then(() => {
        reportLoading.wait(
          getFetcherWithBearer(jwtReport)(SPA_ORIGIN() + `/api/report/json?nocache=${new Date().getTime()}`)
            .then(report => setReport(formatAvailableSignatures(report)))
            .then(reportViewer.open)
        )
      })
      .catch(() => {
        openTokenExpiredDialog()
      })
  }

  const openViewer = () => {
    const wi = window.open('about:blank', '_blank')
    viewLoading.wait(
      shareJwtGetFetcher(jwt, SPA_ORIGIN() + `/api/share/viewerLink?nocache=${new Date().getTime()}`)
        .then(({viewerURL}) => { wi.location.href = viewerURL; })
        .catch(wi.close)
    )
  }

  // OpenViewer for each study
  const openViewerRow = (jwt) => {
    const wi = window.open('about:blank', '_blank')
    validateToken(jwt)
      .then(() => {
        viewLoading.wait(
          shareJwtGetFetcher(jwt,SPA_ORIGIN() + `/api/share/viewerLink?nocache=${new Date().getTime()}`)
            .then(({viewerURL}) => { wi.location.href = viewerURL; })
            .catch(wi.close)
        )
      })
      .catch(() => {
        wi.close()
        openTokenExpiredDialog()
      })
  }

  // Download each study
  const downloadRow = (downloadType, jwt) => () => {
    downloadLoadingIndicator.openWith(jwt)
    validateToken(jwt)
      .then(() => {
        downloadLoading.wait(shareJwtGetFetcher(jwt,SPA_ORIGIN() + `/api/share/downloadLink/${downloadType}?nocache=${new Date().getTime()}`)
          .then(({url}) => checkForDownload(url))
          .then(downloadFile)
        ).then(() => { downloadLoadingIndicator.close(); started.openWith(jwt); started.closeAfter(2000) })
          .catch(downloadError.open)
      })
      .catch(() => {
        downloadLoadingIndicator.close()
        openTokenExpiredDialog()
      })
  }

  const downloadReportRow = (report, jwt, reportJWT) => {
    downloadLoadingIndicator.openWith(jwt)
    fetch(SPA_ORIGIN() + '/api/share/tokenValidate', {
      method: 'HEAD',
      headers: {'Authorization': `Bearer ${reportJWT}`}
    })
      .then(res => {
        if (!res.ok) throw new Error()
        try{
          downloadFile(report)
        }catch(e){
          downloadError.open()
        }finally{
          setTimeout(() => {
            downloadLoadingIndicator.close(); started.openWith(jwt); started.closeAfter(2000)   
          },500)  
        }
      })
      .catch(() => {
        downloadLoadingIndicator.close()
        openTokenExpiredDialog()
      })
  }

  const availableStudies = listOfStudies.filter(s => !s.unavailable)

  if (reportViewer.isOpen && report) {
    return viewReport({report:report.success,reportJWT,closeReport:reportViewer.close,openViewer})
  }
  return <SignInLayout providerInfo={providerInfo} className="notranslate">
    <SharedRecordCard>
      <CardHeader>
        <h1>Access shared records</h1>
      </CardHeader>
      {!isOldSingleShare && <PatientContainer>
        <div>
          <PatientName>Patient: {patientName}</PatientName>
          {!isNaN(dicomDateToDate(patientDOB).getTime()) && <PatientDob>Date of birth: {formatDate(dicomDateToDate(patientDOB), DATE_FORMAT(providerInfo.dateFormat))}</PatientDob>}
        </div>
        {((!sharingOptions || !sharingOptions.viewerDisabled) && availableStudies.length > 1) && <Button icon="view" highlight css="margin-left:1rem;"
          isLoading={isLoadingViewAll} 
          onClick={() => {
            setIsLoadingViewAll(true)
            validateToken(jwt)
              .then(() => {
                if(availableStudies.length > 4){
                  viewImagesDialog.open()
                  setIsLoadingViewAll(false)
                }else{
                  postFetcherWithBearer(jwt)(SPA_ORIGIN() + `/api/share/viewerMultiLink?nocache=${new Date().getTime()}`,{
                    studiesToView: availableStudies.map((s) => {
                      if(s.studyUID && s.patientId){
                        return {
                          studyUID: s.studyUID ? s.studyUID : null,
                          patientId: s.patientId ? s.patientId : null
                        }
                      }else{
                        return {
                          studyUID: s.studyUID ? s.studyUID : null
                        }
                      }})
                      .filter(_.identity)
                      
                  })
                  .then(({viewerURL}) => {
                    const wi = window.open('about:blank', '_blank')
                    wi.location.href = viewerURL;
                    
                  })
                  .catch((error) => {
                    console.log(error)
                  })
                  .finally(() => {
                    setIsLoadingViewAll(false)
                  })
                }
              })
              .catch(() => {
                setIsLoadingViewAll(false)
                openTokenExpiredDialog()
              })
          }}>
            View all images
          </Button>
        }
      </PatientContainer>}
      <ListArea>
      {listOfStudies && listOfStudies.length > 0 && <>
        {listOfStudies.map((item, i) => {
          const isDownloadingRow = downloadLoadingIndicator.isOpen === item.jwt
          const isDownloadStartedRow = started.isOpen === item.jwt
          return <StyledRow key={item.studyUID} unavailable={item.unavailable}> 
            {isOldSingleShare 
              ? <div>
                <PatientName>Patient: {patientName}</PatientName>
                {!isNaN(dicomDateToDate(patientDOB).getTime()) && <PatientDob>Date of birth: {formatDate(dicomDateToDate(patientDOB), DATE_FORMAT(providerInfo.dateFormat))}</PatientDob>}
              </div>
              : <div className='info'>
                <div><b>{item.modalities ? item.modalities + ' - ':''}{(item.studyDescription || '').replace(/\^/g, ' ').trim()+' '}{item.studyDate ? '('+item.studyDate+')' : ''}</b></div>
                {item.images && <div className='rowDetails'>
                  <div>{item.images} images</div>
                </div>}
              </div>
            }
            {item.unavailable 
              ? <div style={{fontSize: '12px'}}>Unavailable</div>
              : <div style={{display: 'flex',justifyContent: 'space-between', flexShrink: 0}}>
              {!isCustodian && shareStudyReport && item.report && viewReport && <ActionLink onClick={() => openReportRow(item.report)}>
                <Icon icon="report"/>
                <span>Report</span>
              </ActionLink>}
              {/* <ActionLink onClick={openViewer}> */}
              {!isCustodian && (!sharingOptions || !sharingOptions.viewerDisabled) && <ActionLink onClick={() => openViewerRow(item.jwt)}>
                <Icon icon="view"/>
                <span>View images</span>
              </ActionLink>}
              {isCustodian && allowImageDownload && (isDownloadingRow ?
                <Loading css="margin-left:1rem;" size={24}/>
                : isDownloadStartedRow ?
                  <DownloadStarted icon="check"/>
                :
                  <ActionLink onClick={downloadRow('regular', item.jwt)}>
                    <Icon icon="download"/>
                    <span>Download images</span>
                  </ActionLink>
              )}
              {!isCustodian && (isDownloadingRow ?
              <Loading css="margin-left:1rem;" size={24}/>
              :
              isDownloadStartedRow ?
                <DownloadStarted icon="check"/>
              :
              shouldShowActionDropdown(shareStudyReport, item, allowImageDownload) && <ActionDropdown onClick={() => downloadMenu.openWith(i)}>
                <Icon icon="arrow-down"/>
                {
                  downloadMenu.isOpen === i &&
                  <ContextMenu>
                    {/* {shareStudyReport && item.report && <Li href={"/api/report/download?t="+item.report} label="Download report"/>} */}
                    {!isCustodian && shareStudyReport && item.report && <Li onClick={() => downloadReportRow("/api/report/download?t="+item.report, item.jwt, item.report)} label="Download report"/>}
                    {/* <Li onClick={download('regular')} label="Download images"/>
                    <Li onClick={download('cd')} label={<>Download images for CD<br/>(with viewer)</>}/> */}
                    {allowImageDownload && <Li onClick={downloadRow('regular',item.jwt)} label="Download images"/>}
                    {!isCustodian && allowImageDownload && <Li onClick={downloadRow('cd',item.jwt)} label={<>Download images for CD<br/>(with viewer)</>}/>}
                  </ContextMenu>
                }
              </ActionDropdown>
              )}
            </div>}
          </StyledRow>
        })}
      </>}
      </ListArea>
      {downloadError.isOpen && <DownloadErrorDialog closeDialog={downloadError.close}/>}
      {tokenExpiredDialog.isOpen && <WarningDialog
        title="Session expired"
        content="Your session has expired. Please refresh the page to continue."
        confirmLabel={false}
        leftButton={<Button highlight onClick={() => window.location.reload()}>Refresh</Button>}
      />}
      <BrandFooter/>
    </SharedRecordCard>
    {viewImagesDialog.isOpen && <Dialog 
      closeDialog={() =>{setSelectedStudies([]); viewImagesDialog.close()}}
      title="View images"
      footer={
        <>
          <Button onClick={() =>{setSelectedStudies([]); viewImagesDialog.close()}}>Cancel</Button>
          <Button highlight isLoading={isLoadingViewAll} disabled={selectedStudies.length === 0}
            onClick={() =>{
              setIsLoadingViewAll(true)
              postFetcherWithBearer(jwt)(SPA_ORIGIN() + `/api/share/viewerMultiLink?nocache=${new Date().getTime()}`,{
                // studiesToView: selectedStudies.map(s => s.studyUID ? s.studyUID : null)
                studiesToView: selectedStudies.map((s) => {
                    if(s.studyUID && s.patientId){
                      return {
                        studyUID: s.studyUID ? s.studyUID : null,
                        patientId: s.patientId ? s.patientId : null
                      }
                    }else{
                      return {
                        studyUID: s.studyUID ? s.studyUID : null
                      }
                    }})
                    .filter(_.identity)
              })
              .then(({viewerURL}) => {
                const wi = window.open('about:blank', '_blank')
                wi.location.href = viewerURL;
                setSelectedStudies([]); 
                viewImagesDialog.close();
              })
              .catch((error) => {
                console.log(error)
              })
              .finally(() => {
                setIsLoadingViewAll(false)
              })
            }}>
              View images
            </Button> 
        </>
      }
    >
      <p><b>Select up to 4 records to launch the images viewer</b></p>
      <ListArea>
        {listOfStudies && listOfStudies.length > 0 && <>
          {listOfStudies.map((item, i) => {

            const selected = selectedStudies.findIndex(s => s.studyUID === item.studyUID) >= 0
            return <StyledRow key={item.studyUID} selectable={!item.unavailable} selected={selected} disabled={!selected && selectedStudies.length >= 4} unavailable={item.unavailable}
              onClick={(e) => {
                if (item.unavailable) return
                e.preventDefault()
                e.stopPropagation()
                return selected 
                  ? setSelectedStudies(selectedStudies.filter(s => s.studyUID !== item.studyUID))
                  : setSelectedStudies([...selectedStudies, item])}
              }> 
              <div className='info-select'>
                <div><b>{item.modalities ? item.modalities + ' - ':''}{(item.studyDescription || '').replace(/\^/g, ' ').trim()+' '}{item.studyDate ? '('+item.studyDate+')' : ''}</b></div>
                {item.images && <div className='rowDetails'>
                  <div>{item.images} images</div>
                </div>}
              </div>
              {item.unavailable 
                ? <div style={{fontSize: '12px'}}>Unavailable</div>
                : <SimpleCheckbox 
                raw
                key={item.studyUID}
                disabled={!selected && selectedStudies.length >= 4}
                value={selectedStudies.findIndex(s => s.studyUID === item.studyUID) >= 0}
              />}
            </StyledRow>
          })}
        </>}
      </ListArea>
    </Dialog>}
    <HelpButton needHelp={needHelp} />
  </SignInLayout>
}